import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FadeUp, Stagger, StaggerItem } from '../components/Motion';
import { Clock, FlaskConical } from 'lucide-react';
import api from '../lib/api';
import { resolveImg } from '../lib/img';

export default function Formation() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith('en') ? 'en' : 'fr';
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get('/formations').then((r) => setItems(r.data)).catch(() => {});
  }, []);

  return (
    <main data-testid="formations-page" className="pt-32">
      <section className="container-x max-w-7xl mx-auto pb-16">
        <FadeUp>
          <p className="overline">{t('formation.overline')}</p>
          <h1 className="mt-4 font-heading text-5xl sm:text-6xl lg:text-7xl text-ink tracking-tight max-w-4xl text-balance">
            {t('formation.title')}
          </h1>
          <p className="mt-6 text-lg text-ink-soft max-w-2xl leading-relaxed">{t('formation.intro')}</p>
        </FadeUp>
      </section>

      <section className="container-x max-w-7xl mx-auto pb-24">
        <Stagger className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
          {items.map((f) => (
            <StaggerItem key={f.id}>
              <article className="bg-white rounded-3xl overflow-hidden border border-border-soft hover-lift h-full" data-testid={`formation-${f.id}`}>
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={resolveImg(f.image)} alt={f[`title_${lng}`]} className="w-full h-full object-cover" />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-2 text-earth text-sm font-mono">
                    <Clock size={14} /> {f.duration}
                  </div>
                  <h3 className="mt-3 font-heading text-2xl text-ink">{f[`title_${lng}`]}</h3>
                  <p className="mt-3 text-ink-soft leading-relaxed">{f[`description_${lng}`]}</p>
                  <div className="mt-5">
                    <p className="overline mb-2">{t('formation.topics_label')}</p>
                    <div className="flex flex-wrap gap-2">
                      {(f[`topics_${lng}`] || []).map((tp) => (
                        <span key={tp} className="text-xs px-3 py-1 rounded-full bg-sand text-forest border border-border-soft">{tp}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </article>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      <section className="bg-forest text-bone py-24">
        <div className="container-x max-w-7xl mx-auto grid gap-10 lg:grid-cols-2 items-center">
          <FadeUp>
            <div className="w-14 h-14 grid place-items-center rounded-2xl bg-bone/10 text-sun mb-5">
              <FlaskConical size={26} strokeWidth={1.6} />
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight">{t('formation.research_title')}</h2>
            <p className="mt-6 text-bone/85 leading-relaxed text-lg">{t('formation.research_text')}</p>
          </FadeUp>
          <FadeUp delay={0.1}>
            <div className="aspect-[4/3] rounded-3xl overflow-hidden">
              <img src="https://customer-assets.emergentagent.com/job_climate-resilience-5/artifacts/g79xasn4_IMG_7094.jpeg" alt="Research" className="w-full h-full object-cover" />
            </div>
          </FadeUp>
        </div>
      </section>
    </main>
  );
}
